import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  BookOpen,
  FileText,
  PlayCircle,
  CheckCircle2,
  Lock,
  Clock,
  Calendar,
  BarChart3,
  ArrowLeft,
  ExternalLink,
  X,
  Video,
  FileAudio,
  Link as LinkIcon,
  Download,
  Loader2
} from "lucide-react";

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [materialModal, setMaterialModal] = useState(null);
  const [materialFileData, setMaterialFileData] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  useEffect(() => {
    fetchCourseData();
  }, [id]);

  const fetchCourseData = async () => {
    try {
      // Fetch course data
      const courseRes = await axios.get(`${API}/courses/${id}`);
      setCourse(courseRes.data);

      // Log access when user views the course
      try {
        await axios.post(`${API}/courses/${id}/access`, { section: 'material' });
      } catch (error) {
        // Silently fail - don't expose internal IDs
      }

      // Fetch materials (handle error independently)
      try {
        const materialsRes = await axios.get(`${API}/materials?course_id=${id}`);
        setMaterials(materialsRes.data);
      } catch (error) {
        console.warn("No materials found or error loading materials:", error);
        setMaterials([]);
      }

      // Fetch quizzes (handle error independently)
      try {
        const quizzesRes = await axios.get(`${API}/quizzes?course_id=${id}`);
        setQuizzes(quizzesRes.data);
      } catch (error) {
        console.warn("No quizzes found or error loading quizzes:", error);
        setQuizzes([]);
      }

      // Get enrollment for students
      if (user?.role === "ESTUDIANTE") {
        try {
          const enrollmentsRes = await axios.get(`${API}/enrollments?course_id=${id}`);
          const userEnrollment = enrollmentsRes.data.find(e => e.user_id === user.id);
          setEnrollment(userEnrollment);
        } catch (error) {
          console.warn("No enrollment found:", error);
        }
      }
    } catch (error) {
      toast.error("Error al cargar el curso");
      navigate("/courses");
    } finally {
      setLoading(false);
    }
  };

  const logAccess = async (section) => {
    try {
      await axios.post(`${API}/courses/${id}/access`, { section });
    } catch (error) {
      console.error("Error logging access:", error);
    }
  };

  const handleMarkMaterialViewed = async () => {
    try {
      await axios.post(`${API}/materials/mark-viewed`, { course_id: id });
      setEnrollment(prev => ({ ...prev, material_viewed: true }));
      toast.success("Material marcado como visto");
      setMaterialModal(null);
    } catch (error) {
      toast.error("Error al marcar el material");
    }
  };

  const openMaterial = async (material) => {
    setMaterialModal(material);
    setMaterialFileData(null);
    logAccess('material');

    // If material has a file, fetch it
    if (material.has_file) {
      setLoadingFile(true);
      try {
        const response = await axios.get(`${API}/materials/${material.id}/file`);
        setMaterialFileData(response.data);
      } catch (error) {
        console.error("Error loading file:", error);
        toast.error("Error al cargar el archivo");
      } finally {
        setLoadingFile(false);
      }
    }
  };

  const downloadFile = (fileData, fileName) => {
    const byteCharacters = atob(fileData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray]);
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const canAccessPretest = () => true;
  const canAccessMaterial = () => !enrollment || enrollment.pretest_completed;
  const canAccessPosttest = () => !enrollment || (enrollment.pretest_completed && enrollment.material_viewed);

  const getPretest = () => quizzes.find(q => q.quiz_type === "pretest");
  const getPosttest = () => quizzes.find(q => q.quiz_type === "posttest");

  const pretestCompleted = Boolean(enrollment?.pretest_completed);
  const materialCompleted = Boolean(enrollment?.material_viewed);
  const posttestCompleted = Boolean(enrollment?.posttest_completed);

  const cardStateClasses = (completed, accessible = true) => {
    if (completed) return "border border-green-200 bg-green-50/80 opacity-90 pointer-events-none";
    if (!accessible) return "opacity-60";
    return "";
  };


  const getMaterialIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'audio': return FileAudio;
      case 'link': return LinkIcon;
      default: return FileText;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-48 skeleton rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const progress = enrollment ? [
    enrollment.pretest_completed,
    enrollment.material_viewed,
    enrollment.posttest_completed
  ].filter(Boolean).length : 0;
  const progressPercent = (progress / 3) * 100;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="course-detail">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Badge variant="secondary">{course?.category}</Badge>
            {enrollment && (
              <Badge className={
                enrollment.status === 'completed' ? 'bg-green-100 text-green-800' :
                enrollment.status === 'failed' ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }>
                {enrollment.status === 'completed' ? 'Completado' :
                 enrollment.status === 'failed' ? 'Reprobado' : 'En Progreso'}
              </Badge>
            )}
          </div>
        </div>
        {['ADMIN', 'PROFESOR'].includes(user?.role) && (
          <Link to={`/courses/${id}/reports`}>
            <Button variant="outline" data-testid="reports-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              Reportes
            </Button>
          </Link>
        )}
      </div>

      {/* Course Banner */}
      <Card className="overflow-hidden">
        <div className="h-48 bg-gradient-to-br from-primary to-blue-600 relative">
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h1 className="text-3xl font-bold font-heading mb-2">{course?.name}</h1>
            <p className="text-white/80 max-w-2xl">{course?.description}</p>
          </div>
        </div>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Inicio: {new Date(course?.start_date).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Fin: {new Date(course?.end_date).toLocaleDateString()}</span>
            </div>
          </div>

          
          {enrollment && (
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600">Progreso del curso</span>
                <span className="font-medium">{Math.round(progressPercent)}%</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Course Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pre-test Section */}
        <Card className={`relative ${cardStateClasses(pretestCompleted, canAccessPretest())}`} data-testid="pretest-section">
          {pretestCompleted && (
            <div className="absolute top-4 right-4 flex items-center gap-2 text-green-700 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              <span>Completado</span>
            </div>
          )}

          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  enrollment?.pretest_completed ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  {enrollment?.pretest_completed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <FileText className="w-5 h-5 text-blue-600" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-lg">Pre-test</CardTitle>
                  <p className="text-sm text-slate-500">Evaluación inicial</p>
                </div>
              </div>
              {!canAccessPretest() && <Lock className="w-5 h-5 text-slate-400" />}
            </div>
          </CardHeader>
          <CardContent>
            {getPretest() ? (
              <>
                <div className="space-y-3 mb-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Preguntas</span>
                    <span className="font-medium">{getPretest().question_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tiempo límite</span>
                    <span className="font-medium">{getPretest().time_limit} min</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Intentos máx.</span>
                    <span className="font-medium">{getPretest().max_attempts}</span>
                  </div>
                </div>

                {pretestCompleted ? (

                  <Button className="w-full" disabled>
                    Completado
                  </Button>
                ) : (
                  <Link to={`/courses/${id}/pretest`}>
                    <Button 
                      className="w-full" 
                      disabled={!canAccessPretest()}
                      onClick={() => logAccess('pretest')}
                      data-testid="start-pretest-btn"
                    >
                      Iniciar Pre-test
                    </Button>
                  </Link>
                )}

              </>
            ) : (
              <div className="text-center py-4 text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No hay pre-test configurado</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Material Section */}
        <Card className={`relative ${cardStateClasses(materialCompleted, canAccessMaterial())}`} data-testid="material-section">
          {materialCompleted && (
            <div className="absolute top-4 right-4 flex items-center gap-2 text-green-700 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              <span>Completado</span>
            </div>
          )}
          {!canAccessMaterial() && (

            <div className="locked-overlay">
              <div className="text-center">
                <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Completa el pre-test primero</p>
              </div>
            </div>
          )}
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  enrollment?.material_viewed ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  {enrollment?.material_viewed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <BookOpen className="w-5 h-5 text-amber-600" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-lg">Material de Estudio</CardTitle>
                  <p className="text-sm text-slate-500">{materials.length} recurso{materials.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {materials.length > 0 ? (
              <div className={`space-y-2 ${materialCompleted ? 'opacity-70 pointer-events-none' : ''}`}>
                {materials.map((material) => {
                  const Icon = getMaterialIcon(material.material_type);
                  return (
                    <button
                      key={material.id}
                      onClick={() => {
                        if (canAccessMaterial() && !materialCompleted) {
                          openMaterial(material);
                        }
                      }}
                      className="w-full flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                      disabled={!canAccessMaterial() || materialCompleted}
                      data-testid={`material-item-${material.id}`}
                    >
                      <Icon className="w-5 h-5 text-slate-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-700 truncate">{material.title}</p>
                        <p className="text-xs text-slate-500">{material.material_type}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400" />
                    </button>
                  );
                })}
                {materialCompleted && (
                  <p className="text-sm text-green-700 text-center mt-4">Material completado</p>
                )}
              </div>
            ) : (

              <div className="text-center py-4 text-slate-500">
                <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No hay material disponible</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Post-test Section */}
        <Card className={`relative ${cardStateClasses(posttestCompleted, canAccessPosttest())}`} data-testid="posttest-section">
          {posttestCompleted && (
            <div className="absolute top-4 right-4 flex items-center gap-2 text-green-700 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              <span>Completado</span>
            </div>
          )}
          {!canAccessPosttest() && (

            <div className="locked-overlay">
              <div className="text-center">
                <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  {!enrollment?.pretest_completed ? 'Completa el pre-test primero' : 'Revisa el material primero'}
                </p>
              </div>
            </div>
          )}
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  enrollment?.posttest_completed ? 'bg-green-100' : 'bg-purple-100'
                }`}>
                  {enrollment?.posttest_completed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <PlayCircle className="w-5 h-5 text-purple-600" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-lg">Post-test</CardTitle>
                  <p className="text-sm text-slate-500">Evaluación final</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {getPosttest() ? (
              <>
                <div className="space-y-3 mb-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Preguntas</span>
                    <span className="font-medium">{getPosttest().question_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tiempo límite</span>
                    <span className="font-medium">{getPosttest().time_limit} min</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Nota mínima</span>
                    <span className="font-medium">{getPosttest().passing_score}%</span>
                  </div>
                  {enrollment && enrollment.posttest_score !== null && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tu calificación</span>
                      <span className={`font-medium ${enrollment.posttest_score >= getPosttest().passing_score ? 'text-green-600' : 'text-red-600'}`}>
                        {enrollment.posttest_score.toFixed(0)}%
                      </span>
                    </div>
                  )}
                </div>
                {posttestCompleted ? (
                  <Button className="w-full" disabled>
                    Completado
                  </Button>
                ) : (
                  <Link to={`/courses/${id}/posttest`}>
                    <Button 
                      className="w-full" 
                      disabled={!canAccessPosttest()}
                      onClick={() => logAccess('posttest')}
                      data-testid="start-posttest-btn"
                    >
                      Iniciar Post-test
                    </Button>
                  </Link>
                )}

              </>
            ) : (
              <div className="text-center py-4 text-slate-500">
                <PlayCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No hay post-test configurado</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Material Modal */}
      <Dialog open={!!materialModal} onOpenChange={() => {
        setMaterialModal(null);
        setMaterialFileData(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <BookOpen className="w-5 h-5" />
              {materialModal?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {loadingFile ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : materialModal?.has_file && materialFileData ? (
              /* Material uploaded as file */
              <div className="space-y-4">
                {materialModal.material_type === 'pdf' ? (
                  /* Show PDF in iframe */
                  <iframe
                    src={`data:application/pdf;base64,${materialFileData.file_data}`}
                    className="w-full h-[60vh] rounded-lg border"
                    title={materialModal.title}
                  />
                ) : (
                  /* Show download button for other file types */
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-slate-700 mb-2">{materialModal.title}</p>
                    <p className="text-slate-600 mb-4">{materialModal.description}</p>
                    <p className="text-sm text-slate-500 mb-6">
                      Archivo: {materialFileData.file_name}
                    </p>
                    <Button 
                      onClick={() => downloadFile(materialFileData.file_data, materialFileData.file_name)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar archivo
                    </Button>
                  </div>
                )}
                {materialModal.description && materialModal.material_type === 'pdf' && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">{materialModal.description}</p>
                  </div>
                )}
              </div>
            ) : (
              /* Material with URL or content (legacy) */
              <>
                {materialModal?.material_type === 'link' && materialModal?.url && (
                  <div className="text-center py-8">
                    <LinkIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 mb-4">{materialModal.description}</p>
                    <a href={materialModal.url} target="_blank" rel="noopener noreferrer">
                      <Button>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Abrir enlace externo
                      </Button>
                    </a>
                  </div>
                )}
                {materialModal?.material_type === 'video' && materialModal?.url && (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <iframe
                      src={materialModal.url}
                      className="w-full h-full"
                      allowFullScreen
                      title={materialModal.title}
                    />
                  </div>
                )}
                {materialModal?.material_type === 'pdf' && materialModal?.url && (
                  <iframe
                    src={materialModal.url}
                    className="w-full h-[60vh] rounded-lg border"
                    title={materialModal.title}
                  />
                )}
                {materialModal?.content && (
                  <div className="prose prose-slate max-w-none p-4">
                    <p className="whitespace-pre-wrap">{materialModal.content}</p>
                  </div>
                )}
                {!materialModal?.url && !materialModal?.content && (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600">{materialModal?.description}</p>
                  </div>
                )}
              </>
            )}
          </div>
          {user?.role === "ESTUDIANTE" && !enrollment?.material_viewed && (
            <div className="pt-4 border-t">
              <Button onClick={handleMarkMaterialViewed} className="w-full" data-testid="mark-viewed-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Marcar como visto y continuar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseDetail;
